import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Card, Input } from '@tracekit/core';
import { useWorkspace, useProposalJobSubmit, useToastStore } from '@tracekit/core';
import { useQuery } from '@tanstack/react-query';
import { 
  Award, FileText, MessageSquare, Info, 
  CheckCircle, AlertCircle, Target, List, Paperclip, RefreshCw,
  Plus, Trash2, Edit3, Loader2, Users, Building2, Save, Clock, Calendar, X, Mail
} from 'lucide-react';
import { CriteriaView } from './CriteriaView';
import { ComplianceMatrixTab } from './ComplianceMatrixTab';
import { AppendicesTab } from './AppendicesTab';
import { formatQuestionTextForDisplay } from '../../utils/formatQuestionText';
import { OpportunityEmailDraftTab } from '../government-data/OpportunityEmailDraftTab';
import { getAPIClient } from '@tracekit/core';

/** Flatten LLM payloads like { points, percentage, criteria } into readable prose for Staff Criteria cards. */
function humanizeStaffScoringSnippet(scoring: unknown): string {
  if (scoring == null || scoring === '') return '';
  if (typeof scoring === 'string') {
    const s = scoring.trim();
    if (s.startsWith('{') && s.includes('"criteria"')) {
      try {
        const parsed = JSON.parse(s) as Record<string, unknown>;
        const bits: string[] = [];
        const c = parsed.criteria;
        if (c != null && c !== '') {
          bits.push(Array.isArray(c) ? (c as unknown[]).map(String).join('; ') : String(c));
        }
        if (parsed.points != null && String(parsed.points) !== 'null') bits.push(`Points: ${String(parsed.points)}`);
        if (parsed.percentage != null && String(parsed.percentage) !== 'null')
          bits.push(`Weight: ${String(parsed.percentage)}`);
        const out = bits.join('. ').trim();
        if (out) return out;
      } catch {
        /* use raw string */
      }
    }
    return s;
  }
  if (typeof scoring === 'object') {
    const o = scoring as Record<string, unknown>;
    const bits: string[] = [];
    const c = o.criteria;
    if (c != null && c !== '')
      bits.push(Array.isArray(c) ? (c as unknown[]).map(String).join('; ') : String(c));
    if (o.points != null && String(o.points) !== 'null') bits.push(`Points: ${String(o.points)}`);
    if (o.percentage != null && String(o.percentage) !== 'null')
      bits.push(`Weight: ${String(o.percentage)}`);
    const out = bits.join('. ').trim();
    if (out) return out;
  }
  return String(scoring);
}

interface TemplatePreviewProps {
  result: any;
  opportunityId: string;
  opportunityTitle?: string;
  onAccept?: () => void; // Optional now
  onClose: () => void;
  onRegenerate?: () => void;
  onProposalSubmitted?: (jobId: string) => void; // New callback for job submission
}

export const TemplatePreview: React.FC<TemplatePreviewProps> = ({ 
  result, 
  opportunityId,
  opportunityTitle = '',
  onAccept, 
  onClose, 
  onRegenerate,
  onProposalSubmitted
}) => {
  const { projectId } = useWorkspace();
  const api = getAPIClient();
  const submitMutation = useProposalJobSubmit();
  const toastStore = useToastStore();
  const addToast = toastStore?.addToast ?? (() => {});

  const [activeTab, setActiveTab] = useState('sections'); // Start on sections tab
  const [isEditMode, setIsEditMode] = useState(true); // Toggle for edit/view mode
  const [proposalName, setProposalName] = useState(opportunityTitle || 'Government Proposal');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [dateFilterRange, setDateFilterRange] = useState<string>('all'); // Date filter for source documents
  const [includePromotionalProjects, setIncludePromotionalProjects] = useState(false);
  const saveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMount = useRef(true);

  // Policy pack section labels (from template generation metadata)
  const staffSectionLabel = result.metadata?.staff_section_label || 'Proposed Key Personnel';
  const companySectionLabel = result.metadata?.company_section_label || 'Company (Capability & Past Projects)';
  const [editableSolicitationConstraints, setEditableSolicitationConstraints] = useState<any>(result.solicitation_constraints || {});
  const [editableProposalOutlinePlan, setEditableProposalOutlinePlan] = useState<any>(result.proposal_outline_plan || {});
  const solicitationConstraints = editableSolicitationConstraints || {};
  const proposalOutlinePlan = editableProposalOutlinePlan || {};
  const prohibitedTopics = solicitationConstraints.prohibited_topics || [];
  const adminRequirements = solicitationConstraints.admin_requirements || [];
  const updateConstraints = (patch: Record<string, any>) => {
    setEditableSolicitationConstraints((prev: any) => {
      const next = { ...(prev || {}), ...patch, review_required: true, status: 'needs_review' };
      if (Object.prototype.hasOwnProperty.call(patch, 'page_limit')) {
        const pageLimit = patch.page_limit;
        setEditableProposalOutlinePlan((plan: any) => ({
          ...(plan || {}),
          body_budget_words: typeof pageLimit === 'number' && pageLimit > 0 ? pageLimit * 430 : null,
          review_required: true,
          status: 'needs_review',
        }));
      }
      return next;
    });
  };

  const outlineModeSummary = useMemo(() => {
    const plan = proposalOutlinePlan as Record<string, any>;
    const slots = [...(plan.slots || []), ...(plan.staff_slots || []), ...(plan.company_slots || [])];
    if (!slots.length) return '';
    const counts: Record<string, number> = {};
    for (const s of slots) {
      const m = String(s?.response_mode || 'unknown');
      counts[m] = (counts[m] || 0) + 1;
    }
    const short: Record<string, string> = {
      scored_capability_narrative: 'Narrative',
      company_experience: 'Company',
      staff_matrix: 'Staff',
      admin_pass_fail: 'Admin',
      cover_letter_assertion: 'Cover',
      forms_only: 'Forms',
      pricing: 'Pricing',
    };
    return Object.entries(counts)
      .filter(([, n]) => n > 0)
      .map(([k, n]) => `${short[k] || k.replace(/_/g, ' ')} (${n})`)
      .join(' · ');
  }, [proposalOutlinePlan]);

  // Build initial criteria: evaluation criteria + synthetic Staff section + synthetic Company section
  const initialCriteria = useMemo(() => {
    const base = result.criteria || [];
    const hasStaff = (result.staff_requirements?.length ?? 0) > 0;
    const companyReq = result.company_requirements || {};
    const hasCompany = (companyReq.capability_statements?.length ?? 0) > 0 || (companyReq.past_projects_enumeration?.length ?? 0) > 0;
    const staffSection = hasStaff ? [{ id: 'staff_section', criterion_id: 'staff_section', title: staffSectionLabel, description: 'One question per proposed staff position.', type: 'staff', kind: 'staff' }] : [];
    const companySection = hasCompany ? [{ id: 'company_section', criterion_id: 'company_section', title: companySectionLabel, description: 'Capability statements and past project descriptions.', type: 'company', kind: 'company' }] : [];
    return [...base, ...staffSection, ...companySection];
  }, [result.criteria, result.staff_requirements, result.company_requirements, staffSectionLabel, companySectionLabel]);

  // Normalize questions: ensure id and criterion_id for staff/company (backend sends section + question)
  const initialQuestions = useMemo(() => {
    const raw = result.questions || [];
    let staffIdx = 0, companyIdx = 0;
    return raw.map((q: any, i: number) => {
      if (q.criterion_id && q.criterion_id !== 'staff_section' && q.criterion_id !== 'company_section') {
        return { ...q, id: q.id || `q_${i}`, text: q.text ?? q.question };
      }
      if (q.section === staffSectionLabel || q.section === 'Staff Resource Details') {
        return { ...q, id: q.id || `staff_${staffIdx++}`, criterion_id: 'staff_section', text: q.text ?? q.question };
      }
      if (q.section === companySectionLabel || q.section === 'Capability Statements' || q.section === 'Past Projects') {
        return { ...q, id: q.id || `company_${companyIdx++}`, criterion_id: 'company_section', text: q.text ?? q.question, _section: q.section };
      }
      return { ...q, id: q.id || `q_${i}`, criterion_id: q.criterion_id || 'Unknown', text: q.text ?? q.question };
    });
  }, [result.questions, staffSectionLabel, companySectionLabel]);

  // Editable state for criteria and questions
  const [editableCriteria, setEditableCriteria] = useState<any[]>(initialCriteria);
  const [editableQuestions, setEditableQuestions] = useState<any[]>(initialQuestions);
  const [editingQuestionIds, setEditingQuestionIds] = useState<Set<string>>(new Set());
  const [questionDrafts, setQuestionDrafts] = useState<Record<string, string>>({});

  // Email Draft tab needs the full opportunity entity (same as Summary modal flow).
  const { data: opportunityEntity } = useQuery<any>({
    queryKey: ['template-preview-opportunity-entity', opportunityId, projectId],
    queryFn: async () => {
      if (!opportunityId || !projectId) return null;
      return api.get<any>(`/entities/${opportunityId}`, {
        params: { project_id: projectId },
      });
    },
    enabled: !!opportunityId && !!projectId,
    staleTime: 60_000,
  });

  // When result loads async (first paint had empty criteria), sync edit buffers once data exists
  useEffect(() => {
    const ic = initialCriteria ?? [];
    const iq = initialQuestions ?? [];
    if (ic.length === 0 && iq.length === 0) return;
    setEditableCriteria((prev) => (prev.length === 0 ? ic : prev));
    setEditableQuestions((prev) => (prev.length === 0 ? iq : prev));
    setEditableSolicitationConstraints((prev: any) =>
      Object.keys(prev || {}).length === 0 ? (result.solicitation_constraints || {}) : prev
    );
    setEditableProposalOutlinePlan((prev: any) =>
      Object.keys(prev || {}).length === 0 ? (result.proposal_outline_plan || {}) : prev
    );
  }, [initialCriteria, initialQuestions]);

  // Group questions by criterion ID for Proposal Sections
  const questionsByCriterion = React.useMemo(() => {
    const grouped: Record<string, any[]> = {};
    editableQuestions.forEach((q: any) => {
      const criterionId = q.criterion_id || 'Unknown';
      if (!grouped[criterionId]) {
        grouped[criterionId] = [];
      }
      grouped[criterionId].push(q);
    });
    return grouped;
  }, [editableQuestions]);

  // KBF-120: debounced "Edits saved" + lastSavedAt (edit mode only; no backend save yet)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!isEditMode) return;
    setSaveStatus('saving');
    if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    saveDebounceRef.current = setTimeout(() => {
      saveDebounceRef.current = null;
      const now = new Date();
      setLastSavedAt(now);
      setSaveStatus('saved');
      addToast({ type: 'success', message: 'Edits saved', duration: 3000 });
    }, 1500);
    return () => {
      if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    };
  }, [editableQuestions, proposalName, editableSolicitationConstraints, editableProposalOutlinePlan, isEditMode, addToast]);

  // Question editing handlers
  const handleAddQuestion = (criterionId: string) => {
    const newId = `new_${Date.now()}`;
    const newQuestion = {
      id: newId,
      criterion_id: criterionId,
      question: '',
      text: '',
      type: 'generated'
    };
    setEditableQuestions([...editableQuestions, newQuestion]);
    setEditingQuestionIds((prev) => new Set(prev).add(newId));
    setQuestionDrafts((prev) => ({ ...prev, [newId]: '' }));
  };

  const handleRemoveQuestion = (questionId: string) => {
    setEditableQuestions(editableQuestions.filter(q => q.id !== questionId));
    setEditingQuestionIds((prev) => {
      const next = new Set(prev);
      next.delete(questionId);
      return next;
    });
    setQuestionDrafts((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const handleUpdateQuestion = (questionId: string, field: string, value: any) => {
    setEditableQuestions(editableQuestions.map(q => 
      q.id === questionId ? { ...q, [field]: value, ...(field === 'question' ? { text: value } : {}) } : q
    ));
  };

  const handleStartEditQuestion = (questionId: string) => {
    const q = editableQuestions.find((x) => x.id === questionId);
    const currentText = q?.question || q?.text || '';
    setQuestionDrafts((prev) => ({ ...prev, [questionId]: currentText }));
    setEditingQuestionIds((prev) => new Set(prev).add(questionId));
  };

  const handleCancelEditQuestion = (questionId: string) => {
    setEditingQuestionIds((prev) => {
      const next = new Set(prev);
      next.delete(questionId);
      return next;
    });
    setQuestionDrafts((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const handleSaveEditQuestion = (questionId: string) => {
    const draft = questionDrafts[questionId] ?? '';
    handleUpdateQuestion(questionId, 'question', draft);
    handleCancelEditQuestion(questionId);
  };

  // Proposal submission handler
  const handleGenerateProposal = async () => {
    if (!projectId || !proposalName.trim()) {
      alert('Please enter a proposal name');
      return;
    }

    if (editableQuestions.length === 0) {
      alert('Please add at least one question');
      return;
    }

    // Validate all questions have text
    const emptyQuestions = editableQuestions.filter(q => !q.question && !q.text);
    if (emptyQuestions.length > 0) {
      alert(`Please fill in all questions (${emptyQuestions.length} empty)`);
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Format questions for API (needs section and question fields)
      const formattedQuestions = editableQuestions.map(q => {
        const criterion = editableCriteria.find(c => 
          c.id === q.criterion_id || c.criterion_id === q.criterion_id
        );
        // Preserve section for staff/company (Staff Resource Details, Capability Statements, Past Projects)
        const section = (q as any)._section || criterion?.title || criterion?.name || q.criterion_id;
        return {
          section,
          question: q.question || q.text,
          criterion_id: q.criterion_id
        };
      });

      const submitResult = await submitMutation.mutateAsync({
        opportunityId,
        projectId,
        clientName: opportunityTitle || proposalName || 'Government Agency',
        questions: formattedQuestions,
        metadata: {
          proposal_name: proposalName,
          include_past_proposals: true,
          include_evidence: true,
          template_source: 'production_template',
          date_filter_range: dateFilterRange, // Source document age filter
          include_promotional_projects: includePromotionalProjects,
          // Include template result so report.metadata.template_result has staff_requirements + company_requirements for Staff Assignments tab
          template_result: {
            staff_requirements: result.staff_requirements || [],
            company_requirements: result.company_requirements || {},
            criteria: editableCriteria,
            questions: formattedQuestions,
            metadata: result.metadata,
            solicitation_constraints: editableSolicitationConstraints,
            proposal_outline_plan: editableProposalOutlinePlan,
          },
        } as any,
      });

      // Notify parent about job submission
      if (onProposalSubmitted) {
        onProposalSubmitted(submitResult.id);
      }
      
      // Close modal - parent will handle navigation to report viewer
      onClose();
      
    } catch (error: any) {
      console.error('Proposal generation failed:', error);
      alert(`Failed to generate proposal: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to get quality score color
  const getQualityScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 dark:text-green-400';
    if (score >= 70) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getQualityScoreBg = (score: number) => {
    if (score >= 90) return 'bg-green-50 dark:bg-green-900/20';
    if (score >= 70) return 'bg-yellow-50 dark:bg-yellow-900/20';
    return 'bg-red-50 dark:bg-red-900/20';
  };

  // Define tabs in correct order
  const tabs = [
    // TAB 0: SECTIONS & QUESTIONS (Unified with edit mode toggle)
    {
      id: 'sections',
      label: (
        <div className="flex items-center gap-2">
          <List className="h-4 w-4" />
          <span>Sections & Questions ({result.criteria?.length || 0})</span>
        </div>
      ),
      content: (
        <div className="min-w-0 space-y-6">
          {(outlineModeSummary || proposalOutlinePlan.status || proposalOutlinePlan.review_required) && (
            <div className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2.5 text-sm text-violet-950 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-100">
              <span className="font-medium">Outline plan</span>
              {proposalOutlinePlan.status ? (
                <span className="ml-2 font-mono text-xs uppercase text-violet-700 dark:text-violet-300">
                  {proposalOutlinePlan.status}
                </span>
              ) : null}
              {proposalOutlinePlan.review_required ? (
                <span className="ml-2 text-amber-700 dark:text-amber-300">· review recommended</span>
              ) : null}
              {proposalOutlinePlan.body_budget_words ? (
                <span className="ml-2 text-violet-800 dark:text-violet-200">
                  · ~{Number(proposalOutlinePlan.body_budget_words).toLocaleString()}w body budget
                </span>
              ) : null}
              {outlineModeSummary ? (
                <p className="mt-1 text-xs text-violet-900/90 dark:text-violet-100/90">{outlineModeSummary}</p>
              ) : null}
            </div>
          )}
          {/* Mode toggle and edit settings — single cohesive block */}
          <div className="min-w-0 space-y-4 pb-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <button
                onClick={() => setIsEditMode(!isEditMode)}
                className={`shrink-0 px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 ${
                  isEditMode
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <Edit3 className="h-4 w-4" />
                {isEditMode ? 'Editing' : 'View Only'}
              </button>
              {isEditMode && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                  <span className="text-xs text-yellow-900 dark:text-yellow-200">
                    Edit mode active - make changes and generate
                  </span>
                </div>
              )}
              <div className="ml-auto flex items-center gap-2">
                {onRegenerate && (
                  <button
                    onClick={onRegenerate}
                    disabled={isSubmitting}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Regenerate template with current settings"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Regenerate
                  </button>
                )}
                <label className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includePromotionalProjects}
                    onChange={(e) => setIncludePromotionalProjects(e.target.checked)}
                    className="rounded"
                  />
                  Include promotional projects
                </label>
                <button
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Review Later
                </button>
                <button
                  onClick={handleGenerateProposal}
                  disabled={isSubmitting || !proposalName.trim() || editableQuestions.length === 0}
                  className="px-4 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-3.5 w-3.5" />
                      Generate Proposal
                    </>
                  )}
                </button>
              </div>
            </div>

            {isEditMode && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 p-4 space-y-4 max-w-full">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Proposal Name
                  </label>
                  <Input
                    type="text"
                    value={proposalName}
                    onChange={(e) => setProposalName(e.target.value)}
                    placeholder="e.g., EN Solicitation WS5414853765"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 flex items-center gap-1.5">
                    <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    Source Age
                  </label>
                  <select
                    value={dateFilterRange}
                    onChange={(e) => setDateFilterRange(e.target.value)}
                    title="Only use documents modified within this timeframe"
                    className="w-full max-w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All</option>
                    <option value="1mo">Last 1 month</option>
                    <option value="3mo">Last 3 months</option>
                    <option value="6mo">Last 6 months</option>
                    <option value="1yr">Last 1 year</option>
                    <option value="3yr">Last 3 years</option>
                    <option value="5yr">Last 5 years</option>
                    <option value="8yr">Last 8 years</option>
                    <option value="10yr">Last 10 years</option>
                  </select>
                  <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                    Filters evidence retrieval during generation
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Content - Conditional based on edit mode */}
          {isEditMode ? (
            <div className="space-y-4">
              {/* EDIT MODE - Editable Questions by Criterion */}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Edit Questions by Criterion</h3>
                <div className="flex items-center gap-3">
                  {saveStatus === 'saving' && (
                    <span className="flex items-center gap-1.5 text-sm text-amber-600 dark:text-amber-400">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Saving…
                    </span>
                  )}
                  {saveStatus === 'saved' && lastSavedAt && (
                    <span className="flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400" title="Edits saved">
                      <Clock className="h-3.5 w-3.5" />
                      Saved at {lastSavedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                  <span className="text-sm text-gray-500 dark:text-theme-text-secondary">
                    {editableQuestions.length} questions across {editableCriteria.length} criteria
                  </span>
                </div>
              </div>

            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 max-h-[600px] overflow-y-auto space-y-4">
              {editableCriteria.map((criterion: any) => {
                const criterionId = criterion.id || criterion.criterion_id;
                const criterionQuestions = editableQuestions.filter(q => q.criterion_id === criterionId);

                return (
                  <Card key={criterionId} className="p-4">
                    {/* Criterion Header */}
                    <div className="flex items-start justify-between mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-sm font-semibold text-primary-600 dark:text-primary-400">
                            {criterionId}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            criterion.kind === 'gate' || criterion.type === 'gate'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          }`}>
                            {criterion.kind === 'gate' || criterion.type === 'gate' ? 'Mandatory' : `${criterion.points || criterion.weight || 0} pts`}
                          </span>
                          {criterion.response_mode && (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                              {String(criterion.response_mode).replace(/_/g, ' ')}
                            </span>
                          )}
                        </div>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-theme-text line-clamp-2">
                          {criterion.title}
                        </h4>
                        {criterion.description && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                            {criterion.description}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleAddQuestion(criterionId)}
                        className="ml-4 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded transition-colors flex items-center gap-1"
                      >
                        <Plus className="h-3 w-3" />
                        Add Question
                      </button>
                    </div>

                    {/* Questions for this Criterion */}
                    <div className="space-y-3">
                      {criterionQuestions.length === 0 ? (
                        <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400 italic">
                          No questions yet. Click "Add Question" to create one.
                        </div>
                      ) : (
                        criterionQuestions.map((q, qIdx) => (
                          <div key={q.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-3">
                            <div className="flex items-start gap-3">
                              <span className="font-mono text-xs font-medium text-gray-500 dark:text-gray-400 mt-2">
                                Q{qIdx + 1}
                              </span>
                              <div className="flex-1">
                                {editingQuestionIds.has(q.id) ? (
                                  <div className="space-y-2">
                                    <textarea
                                      value={questionDrafts[q.id] ?? (q.question || q.text || '')}
                                      onChange={(e) => setQuestionDrafts((prev) => ({ ...prev, [q.id]: e.target.value }))}
                                      placeholder="Enter question text..."
                                      rows={Math.min(Math.max(Math.ceil(((questionDrafts[q.id] ?? q.question ?? q.text ?? '').length) / 80), 2), 6)}
                                      className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-theme-text placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y min-h-[60px]"
                                      style={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}
                                    />
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => handleSaveEditQuestion(q.id)}
                                        className="px-2 py-1 text-xs font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 rounded transition-colors flex items-center gap-1"
                                        title="Save question"
                                      >
                                        <Save className="h-3 w-3" />
                                        Save
                                      </button>
                                      <button
                                        onClick={() => handleCancelEditQuestion(q.id)}
                                        className="px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors flex items-center gap-1"
                                        title="Cancel edit"
                                      >
                                        <X className="h-3 w-3" />
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    <div
                                      className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-theme-text min-h-[60px] whitespace-pre-wrap break-words"
                                      dangerouslySetInnerHTML={{ __html: formatQuestionTextForDisplay(q.question || q.text || '') }}
                                    />
                                    <button
                                      onClick={() => handleStartEditQuestion(q.id)}
                                      className="px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded transition-colors flex items-center gap-1"
                                      title="Edit question"
                                    >
                                      <Edit3 className="h-3 w-3" />
                                      Edit
                                    </button>
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={() => handleRemoveQuestion(q.id)}
                                className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                title="Delete question"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Validation Summary */}
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
              <p className="text-sm text-yellow-900 dark:text-yellow-200">
                <strong>Ready to generate?</strong> Make sure all questions are filled in and your proposal name is set.
              </p>
            </div>
          </div>
          ) : (
            <div className="space-y-4">
              {/* VIEW MODE - Read-only Questions by Criterion (includes Staff & Company sections) */}
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-medium">Proposal Sections</h3>
                <span className="text-sm text-gray-500 dark:text-theme-text-secondary">
                  {editableCriteria.length} sections • {editableQuestions.length} questions
                </span>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 max-h-[600px] overflow-y-auto">
                <div className="space-y-3">
                  {editableCriteria.map((criterion: any, index: number) => {
                    const criterionId = criterion.id || criterion.criterion_id;
                    const criterionQuestions = questionsByCriterion[criterionId] || [];
                    
                    return (
                      <Card key={index} className="p-3">
                        <div className="space-y-2">
                          {/* Criterion Header */}
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono text-sm font-semibold text-primary-600 dark:text-primary-400">
                                  {criterion.id || criterion.criterion_id}
                                </span>
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  criterion.type === 'staff'
                                    ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300'
                                    : criterion.type === 'company'
                                    ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300'
                                    : criterion.kind === 'gate' || criterion.type === 'gate'
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                    : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                }`}>
                                  {criterion.type === 'staff' ? 'Staff' : criterion.type === 'company' ? 'Company' : criterion.kind === 'gate' || criterion.type === 'gate' ? 'Mandatory' : `${criterion.points || criterion.weight || 0} pts`}
                                </span>
                                {criterion.response_mode && criterion.type !== 'staff' && criterion.type !== 'company' && (
                                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                                    {String(criterion.response_mode).replace(/_/g, ' ')}
                                  </span>
                                )}
                              </div>
                              <h4 className="text-sm font-medium text-gray-900 dark:text-theme-text line-clamp-2">
                                {criterion.title?.substring(0, 150) || 'Untitled Criterion'}
                                {criterion.title?.length > 150 ? '...' : ''}
                              </h4>
                            </div>
                          </div>

                          {/* Questions for this criterion */}
                          {criterionQuestions.length > 0 ? (
                            <div className="mt-3 space-y-2 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
                              <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                {criterionQuestions.length} {criterionQuestions.length === 1 ? 'Question' : 'Questions'}:
                              </p>
                              {criterionQuestions.map((q: any, qIdx: number) => (
                                <div key={qIdx} className="flex items-start gap-2 text-sm">
                                  <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30 text-xs font-medium text-primary-700 dark:text-primary-300">
                                    {qIdx + 1}
                                  </span>
                                  <p className="text-gray-700 dark:text-gray-300 text-xs line-clamp-2" dangerouslySetInnerHTML={{ __html: formatQuestionTextForDisplay(q.question || q.text) }} />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500 dark:text-gray-400 italic pl-4">
                              No questions generated for this section
                            </p>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )
    },

    // TAB 1: CRITERIA (Read-only detailed view)
    {
      id: 'criteria',
      label: (
        <div className="flex items-center gap-2">
          <Award className="h-4 w-4" />
          <span>Criteria ({result.criteria?.length || 0})</span>
        </div>
      ),
      content: <CriteriaView criteria={result.criteria || []} />
    },

    // TAB 2: SOLICITATION CONSTRAINTS
    {
      id: 'constraints',
      label: (
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          <span>Constraints</span>
        </div>
      ),
      content: (
        <div className="space-y-4">
          <div className={`border rounded-lg p-4 ${
            solicitationConstraints.review_required
              ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
              : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
          }`}>
            <div className="flex items-start gap-3">
              <AlertCircle className={`h-5 w-5 mt-0.5 ${
                solicitationConstraints.review_required ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'
              }`} />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-theme-text">
                  {solicitationConstraints.review_required ? 'Human review recommended' : 'Constraints extracted'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-theme-text-secondary mt-1">
                  These rules are carried into proposal drafting and validation so administrative items do not become long body narrative.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-4">
              <label className="text-xs text-gray-500 dark:text-gray-400" htmlFor="proposal-page-limit">
                Page Limit
              </label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  id="proposal-page-limit"
                  type="number"
                  min="1"
                  value={solicitationConstraints.page_limit ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    updateConstraints({ page_limit: raw === '' ? null : Number(raw) });
                  }}
                  placeholder="e.g. 6"
                  className="w-24"
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">pages</span>
              </div>
              <select
                value={solicitationConstraints.page_limit_scope || 'body'}
                onChange={(e) => updateConstraints({ page_limit_scope: e.target.value })}
                className="mt-2 w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              >
                <option value="body">Body only</option>
                <option value="body_with_exclusions">Body, excluding forms/appendices</option>
                <option value="total_response">Total response</option>
              </select>
            </Card>
            <Card className="p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Admin Requirements</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-theme-text">{adminRequirements.length}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Forms, signatures, insurance, WCB, delivery rules</p>
            </Card>
            <Card className="p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Body Budget</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-theme-text">
                {proposalOutlinePlan.body_budget_words ? `~${proposalOutlinePlan.body_budget_words} words` : 'Default'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Used as a validation guardrail</p>
            </Card>
          </div>

          <Card className="p-4 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
              <label className="text-sm font-semibold text-red-900 dark:text-red-100 mb-2 block" htmlFor="proposal-prohibited-topics">
                Prohibited Topics
              </label>
              <textarea
                id="proposal-prohibited-topics"
                value={prohibitedTopics.join(', ')}
                onChange={(e) =>
                  updateConstraints({
                    prohibited_topics: e.target.value
                      .split(',')
                      .map((x) => x.trim().replace(/\s+/g, '_'))
                      .filter(Boolean),
                  })
                }
                className="min-h-[72px] w-full rounded border border-red-200 bg-white px-3 py-2 text-sm text-gray-900 dark:border-red-800 dark:bg-gray-900 dark:text-gray-100"
                placeholder="methodology, project_understanding"
              />
              <p className="mt-1 text-xs text-red-800/80 dark:text-red-200/80">
                Comma-separated. These are carried into proposal validation.
              </p>
          </Card>

          {(proposalOutlinePlan.slots?.length ?? 0) > 0 && (
            <Card className="p-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-theme-text mb-3">Response Mode Plan</h4>
              <div className="max-h-[360px] overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700">
                {proposalOutlinePlan.slots.slice(0, 50).map((slot: any, idx: number) => (
                  <div
                    key={slot.criterion_id != null ? `${String(slot.criterion_id)}-${idx}` : `slot-${idx}`}
                    className="py-2 flex items-start justify-between gap-4"
                  >
                    <div>
                      <p className="font-mono text-xs text-primary-600 dark:text-primary-400">{slot.criterion_id}</p>
                      <p className="text-sm text-gray-900 dark:text-theme-text">{slot.title || 'Untitled criterion'}</p>
                    </div>
                    <div className="text-right">
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                        {String(slot.response_mode || '').replace(/_/g, ' ')}
                      </span>
                      {slot.output_budget_words && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">~{slot.output_budget_words} words</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      ),
    },

    // TAB 3: STAFF CRITERIA (derived from RAG + CRAFT; before Appendices)
    {
      id: 'staff_criteria',
      label: (
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4" />
          <span>Staff Criteria ({result.staff_requirements?.length ?? 0})</span>
        </div>
      ),
      content: (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-theme-text-secondary">
            Derived staff positions from the RFP (RAG + CRAFT). Edit questions in <strong>Sections &amp; Questions</strong>.
          </p>
          {(result.staff_requirements?.length ?? 0) === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 italic">No staff requirements extracted.</div>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {result.staff_requirements.map((sr: any, idx: number) => (
                <Card key={sr.position_id || idx} className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className="font-mono text-xs text-primary-600 dark:text-primary-400">{sr.position_id || `S${idx + 1}`}</span>
                      <h4 className="font-medium text-gray-900 dark:text-theme-text mt-1">{sr.role_title || 'Proposed staff'}</h4>
                      {(() => {
                        const snip = humanizeStaffScoringSnippet(sr.scoring_criteria);
                        return snip ? (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-3">
                            {snip.length > 320 ? `${snip.slice(0, 320)}…` : snip}
                          </p>
                        ) : null;
                      })()}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Question for this position appears in Sections &amp; Questions under {staffSectionLabel}.</p>
                </Card>
              ))}
            </div>
          )}
        </div>
      ),
    },

    // TAB 3: COMPANY CRITERIA (derived from RAG + CRAFT; before Appendices)
    {
      id: 'company_criteria',
      label: (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          <span>Company Criteria ({(result.company_requirements?.capability_statements?.length ?? 0) + (result.company_requirements?.past_projects_enumeration?.length ?? 0)})</span>
        </div>
      ),
      content: (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-theme-text-secondary">
            Derived capability and past-project requirements from the RFP. Edit questions in <strong>Sections &amp; Questions</strong>.
          </p>
          {(() => {
            const cap = result.company_requirements?.capability_statements ?? [];
            const past = result.company_requirements?.past_projects_enumeration ?? [];
            if (cap.length === 0 && past.length === 0) {
              return <div className="text-sm text-gray-500 dark:text-gray-400 italic">No company requirements extracted.</div>;
            }
            return (
              <div className="space-y-4 max-h-[500px] overflow-y-auto">
                {cap.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-theme-text mb-2">Capability Statements</h4>
                    <div className="space-y-2">
                      {cap.map((c: any, i: number) => (
                        <Card key={`cap-${i}`} className="p-3">
                          <div className="font-medium text-gray-900 dark:text-theme-text">{c.topic || 'Capability'}</div>
                          {c.scope && <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{c.scope}</p>}
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
                {past.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-theme-text mb-2">Past Projects</h4>
                    <div className="space-y-2">
                      {past.map((p: any, i: number) => (
                        <Card key={`past-${i}`} className="p-3">
                          <div className="text-sm text-gray-900 dark:text-theme-text">
                            {p.max_count != null && Number(p.max_count) > 0 ? (
                              <>Max {Number(p.max_count)} projects (explicit RFQ cap)</>
                            ) : (
                              <>
                                Relevant past projects / experience — no standalone corporate &quot;N projects&quot; cap
                                extracted; selection should follow RFQ wording (e.g., most relevant experience, appendix
                                resume inventories).
                              </>
                            )}
                          </div>
                          {p.criteria && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              {[p.criteria.sector, p.criteria.date_range, p.criteria.min_value].filter(Boolean).join(' • ')}
                            </p>
                          )}
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      ),
    },

    // TAB 4: APPENDICES
    {
      id: 'appendices',
      label: (
        <div className="flex items-center gap-2">
          <Paperclip className="h-4 w-4" />
          <span>Appendices ({result.appendices?.length || result.stats?.appendix_count || 0})</span>
        </div>
      ),
      content: <AppendicesTab appendices={result.appendices || []} />,
    },

    // TAB 5: COMPLIANCE MATRIX (includes staff/company criteria that score staff and company info)
    {
      id: 'compliance',
      label: (
        <div className="flex items-center gap-2">
          <CheckCircle className="h-4 w-4" />
          <span>Compliance Matrix</span>
        </div>
      ),
      content: (
        <ComplianceMatrixTab
          criteria={result.criteria || []}
          staffRequirements={result.staff_requirements || []}
          companyRequirements={result.company_requirements || {}}
        />
      ),
    },

    // TAB 6: EMAIL DRAFT (same functionality as opportunity summary modal)
    {
      id: 'email-draft',
      label: (
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4" />
          <span>Email Draft</span>
        </div>
      ),
      content: opportunityEntity ? (
        <OpportunityEmailDraftTab
          opportunityId={opportunityId}
          projectId={projectId ?? ''}
          opportunity={opportunityEntity}
        />
      ) : (
        <div className="text-sm text-gray-500 dark:text-gray-400 italic py-2">
          Loading opportunity details for email draft...
        </div>
      ),
    },

    // TAB 6: MARKDOWN (hidden per client request)
    // {
    //   id: 'markdown',
    //   label: (
    //     <div className="flex items-center gap-2">
    //       <FileCode className="h-4 w-4" />
    //       <span>Markdown</span>
    //     </div>
    //   ),
    //   content: (
    //     <div className="max-h-96 overflow-y-auto">
    //       <pre className="text-xs font-mono bg-gray-900 dark:bg-gray-950 text-gray-100 p-4 rounded-lg overflow-x-auto">
    //         {result.markdown}
    //       </pre>
    //     </div>
    //   ),
    // },

    // TAB 7: INFO
    {
      id: 'metadata',
      label: (
        <div className="flex items-center gap-2">
          <Info className="h-4 w-4" />
          <span>Info</span>
        </div>
      ),
      content: (
        <div className="space-y-4">
          {/* Quality Score */}
          {result.metadata?.quality_score !== undefined && result.metadata.quality_score !== null && (
            <div
              className={`${getQualityScoreBg(
                result.metadata.quality_score
              )} border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center`}
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Award className={`h-6 w-6 ${getQualityScoreColor(result.metadata.quality_score)}`} />
                <h3 className="text-2xl font-bold text-gray-900 dark:text-theme-text">
                  {(result.metadata.quality_score || 0).toFixed(1)}
                  <span className="text-lg text-gray-600 dark:text-gray-400">/100</span>
                </h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-theme-text-secondary">
                {result.metadata.quality_score >= 90
                  ? '✓ EXCELLENT'
                  : result.metadata.quality_score >= 70
                  ? '⚠ GOOD'
                  : '⚠ NEEDS REVIEW'}
              </p>
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Criteria</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-theme-text">
                {result.criteria?.length || result.stats?.criteria_count || 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {result.stats?.gate_criteria_count || 0} mandatory, {result.stats?.scored_criteria_count || 0} scored
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Questions</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-theme-text">
                {result.questions?.length || result.stats?.question_count || 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                ~{result.stats?.avg_questions_per_criterion || 0} per criterion
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Total Points</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-theme-text">
                {result.stats?.total_points || 0}
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Runtime</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-theme-text">
                {Math.round(result.stats?.runtime_seconds || 0)}s
              </p>
            </div>
          </div>

          {/* RFP Metadata */}
          {result.metadata?.rfp_metadata && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3">RFP Information</h4>
              <div className="space-y-2 text-sm">
                {result.metadata.rfp_metadata.title && (
                  <div>
                    <span className="text-blue-700 dark:text-blue-300 font-medium">Title: </span>
                    <span className="text-blue-900 dark:text-blue-100">{result.metadata.rfp_metadata.title}</span>
                  </div>
                )}
                {result.metadata.rfp_metadata.solicitation_number && (
                  <div>
                    <span className="text-blue-700 dark:text-blue-300 font-medium">Solicitation: </span>
                    <span className="text-blue-900 dark:text-blue-100">{result.metadata.rfp_metadata.solicitation_number}</span>
                  </div>
                )}
                {result.metadata.rfp_metadata.issuing_agency && (
                  <div>
                    <span className="text-blue-700 dark:text-blue-300 font-medium">Agency: </span>
                    <span className="text-blue-900 dark:text-blue-100">{result.metadata.rfp_metadata.issuing_agency}</span>
                  </div>
                )}
                {result.metadata.rfp_metadata.response_deadline && (
                  <div>
                    <span className="text-blue-700 dark:text-blue-300 font-medium">Deadline: </span>
                    <span className="text-blue-900 dark:text-blue-100">{result.metadata.rfp_metadata.response_deadline}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="w-full min-w-0 space-y-4">
      {/* Tabs — scroll inside card so every tab (including Info) stays within the modal */}
      <div className="min-w-0">
        <div className="min-w-0 border-b border-theme-border">
          <nav
            className="flex min-w-0 flex-nowrap gap-x-2 overflow-x-auto overflow-y-hidden pb-px sm:gap-x-4 [-webkit-overflow-scrolling:touch]"
            aria-label="Template preview sections"
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`shrink-0 whitespace-nowrap py-3 px-1.5 text-left sm:py-4 sm:px-2 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-theme-text-secondary hover:text-theme-text hover:border-theme-border'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-4 min-w-0">
          {tabs.find((tab) => tab.id === activeTab)?.content}
        </div>
      </div>

      {/* Action Buttons - Direct Proposal Generation */}
      <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700 mt-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-theme-text-secondary">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span>Template generated successfully</span>
          </div>
          {isEditMode && (
            <>
              {saveStatus === 'saving' && (
                <span className="flex items-center gap-1.5 text-sm text-amber-600 dark:text-amber-400">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Saving…
                </span>
              )}
              {saveStatus === 'saved' && lastSavedAt && (
                <span className="flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400" title="Edits saved">
                  <Save className="h-3.5 w-3.5" />
                  Saved at {lastSavedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </>
          )}
          {onRegenerate && (
            <button
              onClick={onRegenerate}
              disabled={isSubmitting}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Regenerate template with current settings"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Regenerate
            </button>
          )}
        </div>
        
        <div className="flex gap-3 items-center">
          <label className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={includePromotionalProjects}
              onChange={(e) => setIncludePromotionalProjects(e.target.checked)}
              className="rounded"
            />
            Include promotional projects
          </label>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Review Later
          </button>
          <button
            onClick={handleGenerateProposal}
            disabled={isSubmitting || !proposalName.trim() || editableQuestions.length === 0}
            className="px-6 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <FileText className="h-4 w-4" />
                Generate Proposal
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
